import { useAtomValue,  } from 'jotai';

import {
	InventoryItemCondition
} from '@ekhodealer/ekho-common';
import { Stack, Text } from '@ekhodealer/ekho-common/rev';

import type {
	ServicesProviderInventory} from '@ekhodealer/ekho-common';
import type { UseQueryResult } from '@tanstack/react-query';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { getDaysElapsed } from '@common/lib/time';

import { useGetInventoryItem } from '../../../api/useGetInventoryItem';
import { drawerAtom,  } from '../../../atoms/editDrawerAtoms';
import { getFulfillmentOptionsLabel } from '../../../lib/utils';

export const UnitDetails = () => {
	// Drawer state atoms
	const { selectedItemId} = useAtomValue(drawerAtom);

	// React query hooks
	const { data: inventoryItem, isLoading} = useGetInventoryItem(selectedItemId || '');
	const { data: servicesProvider } = useCurrentServicesProvider() as UseQueryResult<
		ServicesProviderInventory,
		Error
	>;

	// Show loading if actively loading OR if we have an ID but no data yet
	const isDataLoading = isLoading || Boolean(selectedItemId && !inventoryItem);

	// Static unit details from database
	const {
		newUsed: condition,
		odometerReading,
		odometerUnit,
		vin,
		stockNumber,
		dateAddedToInventory,
		description,
		fulfillmentOptions,
	} = inventoryItem || {};

	const odometerValue =
		odometerReading !== null && odometerReading !== undefined
			? `${odometerReading.toString()} ${odometerUnit || ''}`.trim()
			: undefined;

	return (
		<AccordionSection title='Unit details' defaultExpanded>
			<Stack itemsSpacing='16'>
				<InfoLineItem title='VIN' value={vin} isCopyable isLoading={isDataLoading} />
				<InfoLineItem title='Stock' value={stockNumber} isCopyable isLoading={isDataLoading} />
				<InfoLineItem
					title='Inventory age'
					value={dateAddedToInventory ? `${getDaysElapsed(dateAddedToInventory)} days` : undefined}
					tooltip='Calculated automatically based on the in-stock date from your DMS.'
					isLoading={isDataLoading}
				/>
				<InfoLineItem
					title='Condition'
					value={
						condition === InventoryItemCondition.NEW
							? 'New'
							: condition === InventoryItemCondition.USED
							? 'Used'
							: condition === InventoryItemCondition.CONSIGNED
							? 'Consigned'
							: ''
					}
					isLoading={isDataLoading}
				/>
				<InfoLineItem title='Odometer' value={odometerValue} isLoading={isDataLoading} />
			<InfoLineItem
				title='Fulfillment options'
				value={getFulfillmentOptionsLabel(fulfillmentOptions, servicesProvider)}
				isLoading={isDataLoading}
			/>
			<InfoLineItem
				title='Description'
				content={
					description ? (
						<div style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
							<Text size='bodySmall'>{description}</Text>
						</div>
					) : (
						<Text size='bodySmall'>-</Text>
					)
				}
				isLoading={isDataLoading}
			/>
			</Stack>
		</AccordionSection>
	);
};
